import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Link as MuiLink,
  Typography,
} from "@mui/material";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import AgentToolLoadout from "../../AgentToolLoadout";
import { getMcpBrandStyle } from "../../../McpServers/mcpServerBrands";
import { resolveMcpServerBrandId } from "../../../McpServers/mcpServerTemplates";
import { filterToolCategories, formatToolNameAsLabel } from "../toolCatalog";
import AgentConfigSection from "../AgentConfigSection";
import useMcpServersStore from "../../../McpServers/useMcpServersStore";
import { send_request } from "../../../../utils/Request";

const MCP_SERVER_BASE_PATH = "ai-service/mcp/server";

/** Keep management fields (url, lastError) when /catalog returns a slimmer payload. */
function mergeMcpCatalogEntries(previous, fresh) {
  if (!Array.isArray(fresh) || fresh.length === 0) {
    return Array.isArray(previous) ? previous : [];
  }
  if (!Array.isArray(previous) || previous.length === 0) {
    return fresh;
  }
  const prevById = new Map(
    previous.map((server) => [String(server.serverId), server])
  );
  return fresh.map((entry) => {
    const prev = prevById.get(String(entry.serverId));
    if (!prev) return entry;
    return {
      ...prev,
      ...entry,
      url: prev.url ?? entry.url,
      lastError: entry.lastError ?? prev.lastError,
    };
  });
}

function mcpToolKey(serverId, remoteName) {
  return `${String(serverId || "").trim()}::${String(remoteName || "").trim()}`;
}

function parseMcpToolKey(key) {
  const separator = key.indexOf("::");
  if (separator <= 0) return null;
  return {
    serverId: key.slice(0, separator),
    remoteName: key.slice(separator + 2),
  };
}

function refsToSelection(mcpServerRefs) {
  const map = new Map();
  (Array.isArray(mcpServerRefs) ? mcpServerRefs : []).forEach((ref) => {
    const serverId = String(ref?.serverId || "").trim();
    if (!serverId) return;
    const tools = Array.isArray(ref?.toolAllowlist) ? ref.toolAllowlist : [];
    map.set(serverId, new Set(tools.map((t) => String(t))));
  });
  return map;
}

function selectionToRefs(selection) {
  const refs = [];
  selection.forEach((toolSet, serverId) => {
    if (toolSet.size === 0) return;
    refs.push({ serverId, toolAllowlist: Array.from(toolSet) });
  });
  return refs;
}

function catalogToCategories(catalog) {
  return (Array.isArray(catalog) ? catalog : []).map((server) => {
    const brandId = resolveMcpServerBrandId(server);
    const brand = getMcpBrandStyle(brandId);
    return {
      title: server.name,
      color: brand.accent,
      hideCategoryConfigLabel: true,
      server,
      tools: (server.tools || []).map((tool) => ({
        name: mcpToolKey(server.serverId, tool.remoteName),
        label: formatToolNameAsLabel(tool.remoteName),
        desc: tool.description || "",
        categoryTitle: server.name,
        Icon: HubOutlinedIcon,
      })),
    };
  });
}

export default function McpToolsSection({ mcpServerRefs, setMcpServerRefs }) {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [toolSearchQuery, setToolSearchQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    const cachedFromStore = useMcpServersStore.getState().servers;

    if (cachedFromStore.length > 0) {
      setCatalog(cachedFromStore);
      setLoading(false);
    }

    (async () => {
      let hasCachedData = cachedFromStore.length > 0;

      if (!hasCachedData) {
        const servers = await useMcpServersStore.getState().fetchAll();
        if (cancelled) return;
        if (servers.length === 0 && useMcpServersStore.getState().error) {
          setError(useMcpServersStore.getState().error);
          setCatalog([]);
        } else {
          setCatalog(servers);
          setError(null);
          hasCachedData = servers.length > 0;
        }
        setLoading(false);
      }

      setRefreshing(true);
      const res = await send_request(
        `${MCP_SERVER_BASE_PATH}/catalog`,
        "",
        {},
        "GET"
      );
      if (cancelled) return;
      if (res?.response || res instanceof Error) {
        if (!hasCachedData) {
          setError((prev) => prev || "Failed to load MCP servers.");
        }
      } else {
        const fresh = Array.isArray(res?.data) ? res.data : [];
        setCatalog((prev) => mergeMcpCatalogEntries(prev, fresh));
        setError(null);
      }
      setRefreshing(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const toolCategories = useMemo(
    () => catalogToCategories(catalog),
    [catalog]
  );

  const filteredToolCategories = useMemo(
    () => filterToolCategories(toolCategories, toolSearchQuery),
    [toolCategories, toolSearchQuery]
  );

  const selection = useMemo(
    () => refsToSelection(mcpServerRefs),
    [mcpServerRefs]
  );

  const enabledTools = useMemo(() => {
    const keys = [];
    selection.forEach((toolSet, serverId) => {
      toolSet.forEach((remoteName) => {
        keys.push(mcpToolKey(serverId, remoteName));
      });
    });
    return keys;
  }, [selection]);

  const catalogServerIds = useMemo(
    () =>
      new Set(
        (Array.isArray(catalog) ? catalog : []).map((server) =>
          String(server.serverId)
        )
      ),
    [catalog]
  );

  const commit = (nextSelection) => {
    const refs = selectionToRefs(nextSelection).filter((ref) =>
      catalogServerIds.has(String(ref.serverId))
    );
    setMcpServerRefs(refs);
  };

  const toggleTool = (toolKey) => {
    const parsed = parseMcpToolKey(toolKey);
    if (!parsed) return;
    const next = new Map();
    selection.forEach((set, id) => next.set(id, new Set(set)));
    const set = next.get(parsed.serverId) || new Set();
    if (set.has(parsed.remoteName)) {
      set.delete(parsed.remoteName);
    } else {
      set.add(parsed.remoteName);
    }
    next.set(parsed.serverId, set);
    commit(next);
  };

  const setCategoryToolsEnabled = (toolNames, enabled) => {
    const next = new Map();
    selection.forEach((set, id) => next.set(id, new Set(set)));
    (Array.isArray(toolNames) ? toolNames : []).forEach((toolKey) => {
      const parsed = parseMcpToolKey(toolKey);
      if (!parsed) return;
      const toolSet = next.get(parsed.serverId) || new Set();
      if (enabled) {
        toolSet.add(parsed.remoteName);
      } else {
        toolSet.delete(parsed.remoteName);
      }
      next.set(parsed.serverId, toolSet);
    });
    commit(next);
  };

  const renderCategoryConfig = (category) => {
    const server = category.server;
    if (!server) return null;

    if (server.status === "ERROR") {
      return (
        <Alert severity="error" sx={{ py: 0.5 }}>
          {server.lastError || "This server is in an error state."}
        </Alert>
      );
    }

    if ((server.tools || []).length === 0) {
      return (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          No tools discovered yet. Refresh the server on the{" "}
          <MuiLink href="/mcp_servers" target="_blank" rel="noopener noreferrer">
            MCP Servers
          </MuiLink>{" "}
          page.
        </Typography>
      );
    }

    return null;
  };

  return (
    <AgentConfigSection id="mcpIntegrations">
      <Divider sx={{ borderColor: "divider", mb: 2.5 }} />

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {catalog.length === 0 ? (
            <Box
              sx={{
                p: 3,
                textAlign: "center",
                color: "text.secondary",
                border: 1,
                borderColor: "divider",
                borderStyle: "dashed",
                borderRadius: 1,
              }}
            >
              <HubOutlinedIcon sx={{ fontSize: 32, opacity: 0.5, mb: 1 }} />
              <Typography sx={{ fontSize: "13px" }}>
                No MCP servers connected yet. Add one on the{" "}
                <MuiLink
                  href="/mcp_servers"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  MCP Servers
                </MuiLink>{" "}
                page.
              </Typography>
            </Box>
          ) : (
            <>
              {catalog.some((server) => server.status === "ERROR") && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  One or more MCP servers need attention. Open the MCP Servers
                  page to review connection errors.
                </Alert>
              )}
              {refreshing && (
                <Typography
                  sx={{ fontSize: "11px", color: "text.disabled", mb: 1 }}
                >
                  Syncing tool lists from MCP servers…
                </Typography>
              )}
              <AgentToolLoadout
                toolCategories={toolCategories}
                inventoryCategories={filteredToolCategories}
                enabledTools={enabledTools}
                onToggleTool={toggleTool}
                onBulkSetToolsEnabled={setCategoryToolsEnabled}
                toolSearchQuery={toolSearchQuery}
                setToolSearchQuery={setToolSearchQuery}
                loadingToolCatalog={false}
                includeCustomToolsCategory={false}
                renderCategoryConfig={renderCategoryConfig}
              />
            </>
          )}
        </>
      )}
    </AgentConfigSection>
  );
}

McpToolsSection.propTypes = {
  mcpServerRefs: PropTypes.array.isRequired,
  setMcpServerRefs: PropTypes.func.isRequired,
};
